import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchWorkChildren, fetchWorkSections, type SeoWorkType } from './api';

const LEVEL_COLORS = ['', '#EBEBEB', '#CDCDCD', '#A5A1A1', '#807D7D', '#5F5B5B', '#5F5050', '#473131', '#251E1E', '#1A1818'];

const isRoot = (work: SeoWorkType) => !work.parent_id;

interface DragProps {
  dragging: SeoWorkType | null;
  onDragChange: (work: SeoWorkType | null) => void;
  /** `target === null` means "make root". */
  onDropOn: (source: SeoWorkType, target: SeoWorkType | null) => void;
}

interface NodeProps extends DragProps {
  work: SeoWorkType;
  level: number;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

const WorkNode = ({ work, level, selectedId, onSelect, dragging, onDragChange, onDropOn }: NodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isOver, setIsOver] = useState(false);

  const children = useQuery({
    queryKey: ['seo-work', 'children', work.id],
    queryFn: () => fetchWorkChildren(work.id),
    enabled: expanded,
  });

  const canDrop = dragging !== null && dragging.id !== work.id;

  return (
    <>
      <div
        draggable={!isRoot(work)}
        onDragStart={() => onDragChange(work)}
        onDragEnd={() => onDragChange(null)}
        onDragOver={(e) => canDrop && e.preventDefault()}
        onDragEnter={() => canDrop && setIsOver(true)}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsOver(false);
          if (dragging && canDrop) onDropOn(dragging, work);
        }}
        style={{
          padding: `4px 0 4px ${level}em`,
          borderLeft: level ? `solid ${LEVEL_COLORS[Math.min(level, 9)]}` : undefined,
          background: isOver ? '#deebff' : undefined,
        }}
      >
        <span
          className="pull-left"
          style={{ cursor: 'pointer', marginRight: 6 }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <small className="label bg-blue">{expanded ? '-' : '+'}</small>
        </span>
        <span
          style={{ cursor: 'pointer', fontWeight: selectedId === work.id ? 700 : 400 }}
          onClick={() => onSelect(work.id)}
        >
          {work.name}
        </span>
      </div>
      {expanded &&
        children.data?.map((child) => (
          <WorkNode
            key={child.id}
            work={child}
            level={level + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            dragging={dragging}
            onDragChange={onDragChange}
            onDropOn={onDropOn}
          />
        ))}
    </>
  );
};

interface TreeProps extends DragProps {
  selectedId: number | null;
  onSelect: (id: number) => void;
}

/** Lazy-loaded work types tree. Non-root items can be dragged onto another item (or the root zone) to move them. */
export const WorkTree = ({ selectedId, onSelect, dragging, onDragChange, onDropOn }: TreeProps) => {
  const [isOverRoot, setIsOverRoot] = useState(false);
  const sections = useQuery({ queryKey: ['seo-work', 'sections'], queryFn: fetchWorkSections });

  const canMakeRoot = dragging !== null && !isRoot(dragging);

  return (
    <div>
      {canMakeRoot && (
        <div
          className="callout callout-info"
          style={{ background: isOverRoot ? '#deebff' : undefined, color: '#333' }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsOverRoot(true)}
          onDragLeave={() => setIsOverRoot(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsOverRoot(false);
            onDropOn(dragging, null);
          }}
        >
          Перетащите сюда, чтобы сделать корневым элементом
        </div>
      )}
      {sections.isLoading && <p>Загрузка…</p>}
      {sections.error && <div className="alert alert-danger">Ошибка загрузки услуг</div>}
      {sections.data?.map((work) => (
        <WorkNode
          key={work.id}
          work={work}
          level={0}
          selectedId={selectedId}
          onSelect={onSelect}
          dragging={dragging}
          onDragChange={onDragChange}
          onDropOn={onDropOn}
        />
      ))}
    </div>
  );
};
